/**
 * InvTrac API Configuration
 * Environment-based URL management for the InvTrac inventory system.
 * Unlike the CaseTrac API config, this returns null instead of throwing
 * when unconfigured — InvTrac is optional and CaseTrac must never be blocked.
 */

import { logAPI } from '../utils/logger';

/**
 * Gets the InvTrac API base URL, or null if not configured.
 * @returns The validated InvTrac API base URL, or null
 */
export const getInvTracBaseUrl = (): string | null => {
  let url: string | undefined;

  if (process.env.REACT_APP_INVTRAC_API_BASE_URL) {
    url = process.env.REACT_APP_INVTRAC_API_BASE_URL;
  } else if (process.env.NODE_ENV === 'development') {
    url = 'http://localhost:5062/api/v1';
  } else {
    // In production, InvTrac URL is optional — return null instead of throwing
    logAPI.debug('REACT_APP_INVTRAC_API_BASE_URL not configured — InvTrac integration disabled');
    return null;
  }

  // Remove trailing slash
  url = url.replace(/\/$/, '');

  // Enforce HTTPS in production
  if (process.env.NODE_ENV === 'production' && url.startsWith('http://')) {
    logAPI.error('InvTrac API URL must use HTTPS in production');
    return null;
  }

  return url;
};

/**
 * The InvTrac API base URL — null if not configured.
 * When null, all InvTrac API methods return null gracefully.
 */
export const INVTRAC_API_BASE_URL = getInvTracBaseUrl();

/**
 * Gets the OAuth2 scope to request when calling the InvTrac API, or null if
 * not configured.
 *
 * InvTrac is a separate Azure AD app registration with its own audience, so
 * CaseTrac must request a token scoped to InvTrac (not its own API) — a token
 * minted for CaseTrac's audience is rejected by InvTrac with 401. The scope is
 * derived from the InvTrac app registration's client ID:
 * `api://{invtracClientId}/access_as_user`.
 *
 * @returns The InvTrac delegated scope, or null if the client ID is unset.
 */
export const getInvTracApiScope = (): string | null => {
  const clientId = process.env.REACT_APP_INVTRAC_AZURE_CLIENT_ID;
  if (!clientId) {
    logAPI.debug('REACT_APP_INVTRAC_AZURE_CLIENT_ID not configured — falling back to default API scope for InvTrac calls');
    return null;
  }
  return `api://${clientId}/access_as_user`;
};

/**
 * The InvTrac API scope — null if not configured.
 * When null, callers fall back to the default CaseTrac API token.
 */
export const INVTRAC_API_SCOPE = getInvTracApiScope();
